import React from "react";
import {
    Badge,
    Button,
    Card,
    Group,
    Stack,
    Text,
    Title,
} from "@mantine/core";

import {
    IconEdit,
    IconEye,
    IconUsers,
} from "@tabler/icons-react";

import { HotelBookingDto } from "../../api/hotelBookingApi";

import {
    formatDateTime,
    formatMoney,
    getDeadlineBadgeColor,
    getDeadlineText,
    getRoomingProgress,
    getStatusColor,
    getStatusText,
    getDaysUntil,
    normalizeStatus,
} from "../../utils/hotelBookingUtils";

import {
    BookingAction,
    HotelBookingTexts,
} from "./HotelBookingTypes";

import BookingStatusTimeline from "./BookingStatusTimeline";
import RoomingProgressBar from "./RoomingProgressBar";
import PaymentDeadlineBadge from "./PaymentDeadlineBadge";

type Props = {
    booking: HotelBookingDto;
    t: HotelBookingTexts;
    language: "en" | "da";

    onView: BookingAction;
    onEdit?: BookingAction;
    onPay: BookingAction;
    onRooming: BookingAction;

    onInvoice?: BookingAction;
};

export default function BookingCard({
    booking,
    t,
    language,
    onView,
    onEdit,
    onPay,
    onRooming,
}: Props) {
    const progress = getRoomingProgress(booking);

    const status = normalizeStatus(
        booking.status
    );

    const canEdit =
        !booking.isPaid &&
        !booking.paidAt &&
        (
            status === "pendingapproval" ||
            status === "awaitingpayment"
        );

    const roomingDaysLeft = getDaysUntil(
        booking.roomingDeadline
    );

    const roomingDeadlineText =
        getDeadlineText(
            "Rooming list",
            booking.roomingDeadline,
            language
        );

    const paymentDeadline =
        booking.paymentDeadline;

    return (
        <Card
            withBorder
            radius="md"
            p="md"
        >
            <Group
                justify="space-between"
                align="flex-start"
                wrap="nowrap"
            >
                <Stack
                    gap={8}
                    style={{ flex: 1 }}
                >
                    <Group
                        gap="xs"
                        align="center"
                    >
                        <Title order={4}>
                            {t.booking} #
                            {booking.hotelBookingID}
                        </Title>

                        <Badge
                            color={getStatusColor(
                                booking.status
                            )}
                            variant="filled"
                        >
                            {getStatusText(
                                booking.status,
                                language
                            )}
                        </Badge>
                    </Group>

                    <BookingStatusTimeline
                        status={booking.status}
                        language={language}
                    />

                    <Text
                        size="sm"
                        c="dimmed"
                    >
                        {t.hotel}:{" "}
                        <strong>
                            {booking.hotelName ||
                                booking.hotelID}
                        </strong>
                    </Text>

                    <Group gap="xl">
                        <Text size="sm">
                            {t.rooms}:{" "}
                            <strong>
                                {booking.rooms
                                    ?.length ?? 0}
                            </strong>
                        </Text>

                        <Text size="sm">
                            {t.total}:{" "}
                            <strong>
                                {formatMoney(
                                    booking.totalPrice
                                )}
                            </strong>
                        </Text>

                        <Text size="sm">
                            {t.created}:{" "}
                            <strong>
                                {formatDateTime(
                                    booking.createdAt
                                )}
                            </strong>
                        </Text>
                    </Group>

                    <Group gap="xs">
                        <PaymentDeadlineBadge
                            paymentDeadline={
                                paymentDeadline
                            }
                            language={language}
                        />

                        {booking.roomingDeadline && (
                            <Badge
                                color={getDeadlineBadgeColor(
                                    roomingDaysLeft
                                )}
                                variant="light"
                            >
                                {
                                    roomingDeadlineText
                                }
                            </Badge>
                        )}
                    </Group>

                    <RoomingProgressBar
                        filledGuests={
                            progress.filledGuests
                        }
                        totalGuests={
                            progress.totalGuests
                        }
                        missingGuests={
                            progress.missingGuests
                        }
                        language={language}
                    />

                    {booking.roomingDeadline && (
                        <Text
                            size="xs"
                            c="dimmed"
                        >
                            {t.roomingDeadline}:{" "}
                            {formatDateTime(
                                booking.roomingDeadline
                            )}
                        </Text>
                    )}
                </Stack>

                <Group
                    gap="xs"
                    justify="flex-end"
                >
                    <Button
                        variant="light"
                        leftSection={
                            <IconEye size={16} />
                        }
                        onClick={() =>
                            onView(booking)
                        }
                    >
                        {t.view}
                    </Button>

                    {canEdit && onEdit && (
                        <Button
                            variant="light"
                            color="orange"
                            leftSection={
                                <IconEdit
                                    size={16}
                                />
                            }
                            onClick={() =>
                                onEdit(booking)
                            }
                        >
                            {language === "en"
                                ? "Edit"
                                : "Ret"}
                        </Button>
                    )}

                    {status ===
                        "awaitingpayment" && (
                            <Group gap="xs">
                                {booking.isPaid ? (
                                    <Badge color="green">
                                        Paid
                                    </Badge>
                                ) : booking.paymentLink ? (
                                    <Button
                                        color="green"
                                        onClick={() =>
                                            onPay(
                                                booking
                                            )
                                        }
                                    >
                                        {t.payNow}
                                    </Button>
                                ) : (
                                    <Badge color="gray">
                                        Awaiting payment
                                        link
                                    </Badge>
                                )}
                            </Group>
                        )}

                    {(status === "paid" ||
                        status ===
                        "roomassignmentopen") && (
                            <Button
                                color="violet"
                                leftSection={
                                    <IconUsers
                                        size={16}
                                    />
                                }
                                onClick={() =>
                                    onRooming(
                                        booking
                                    )
                                }
                            >
                                {status === "paid"
                                    ? t.fillGuests
                                    : t.continueRooming}
                            </Button>
                        )}
                </Group>
            </Group>
        </Card>
    );
}